import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmProvider";
import DisponibilitaBadge from "@/components/DisponibilitaBadge";

export default function VehicleCestino() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/vehicles/cestino");
      setItems(r.data?.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onRipristina = async (v) => {
    const ok = await confirm({
      title: "Ripristina veicolo",
      message: `Vuoi ripristinare ${v.codice} ${v.marca} ${v.modello}?`,
      variant: "approve",
      confirmLabel: "SI, ripristina",
    });
    if (!ok) return;
    try {
      await api.post(`/vehicles/${v.id}/ripristina`);
      toast.success("Veicolo ripristinato");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const onHardDelete = async (v) => {
    // Sprint FIX-APPRONTAMENTO-CESTINO — rimosso il vincolo "solo VIRTUAL".
    // Il backend (post VEICOLI-DELETE) consente l'hard-delete di qualsiasi
    // veicolo nel cestino senza pratiche bloccanti (impegnata/venduta/consegnata).
    // can_delete è esposto da _enrich_vehicle e usato anche per disabilitare il
    // bottone con tooltip nei rari casi di pratica bloccante residua.
    if (v.can_delete === false) {
      toast.error("Veicolo collegato a una pratica impegnata/venduta/consegnata, non eliminabile");
      return;
    }
    const ok = await confirm({
      title: "Elimina definitivamente",
      message: `Stai per eliminare DEFINITIVAMENTE ${v.codice}.\nQuesta azione è irreversibile.`,
      confirmLabel: "SI, elimina definitivamente",
    });
    if (!ok) return;
    const conf = window.prompt("Per confermare, scrivi: ELIMINA");
    if (conf !== "ELIMINA") {
      toast.error("Annullato (testo di conferma errato)");
      return;
    }
    try {
      await api.delete(`/vehicles/${v.id}/hard`);
      toast.success("Veicolo eliminato definitivamente");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  return (
    <div className="space-y-5" data-testid="vehicle-cestino-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/vehicles")}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Torna ai veicoli
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-semibold text-slate-900">Cestino veicoli</h1>
            <p className="text-slate-500 text-sm">Veicoli soft-deleted. Solo developer/admin possono ripristinare o eliminare definitivamente.</p>
          </div>
        </div>
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500">Caricamento...</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <Trash2 className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <div className="text-slate-700 font-medium">Cestino vuoto</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[11px] uppercase text-slate-500">
                  <th className="p-2">Disp.</th>
                  <th className="p-2">Codice</th>
                  <th className="p-2">Targa</th>
                  <th className="p-2">Marca / Modello</th>
                  <th className="p-2">Eliminato il</th>
                  <th className="p-2">Eliminato da</th>
                  <th className="p-2">Motivo</th>
                  <th className="p-2 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {items.map((v) => (
                  <tr key={v.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`trash-row-${v.codice}`}>
                    <td className="p-2"><DisponibilitaBadge status={v.status} size="sm" /></td>
                    <td className="p-2 font-mono text-blue-700">{v.codice}</td>
                    <td className="p-2">{v.targa || "—"}</td>
                    <td className="p-2">{v.marca} {v.modello}</td>
                    <td className="p-2 text-xs text-slate-500">{v.deleted_at?.slice(0, 16).replace("T", " ")}</td>
                    <td className="p-2 text-xs text-slate-500">{v.deleted_by || "—"}</td>
                    <td className="p-2 text-xs">{v.delete_reason || "—"}</td>
                    <td className="p-2 text-right space-x-2 whitespace-nowrap">
                      <Button variant="outline" size="sm" onClick={() => onRipristina(v)} data-testid={`ripristina-${v.codice}`}>
                        <RotateCcw className="h-3 w-3 mr-1" /> Ripristina
                      </Button>
                      <Button
                        variant="outline" size="sm"
                        onClick={() => onHardDelete(v)}
                        disabled={v.can_delete === false}
                        title={v.can_delete === false
                          ? "Veicolo collegato a una pratica impegnata/venduta/consegnata: non eliminabile"
                          : "Elimina definitivamente"}
                        className="text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:hover:bg-transparent"
                        data-testid={`hard-delete-${v.codice}`}>
                        <Trash2 className="h-3 w-3 mr-1" /> Elimina
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
